import { FC } from 'react';
import { Avatar, Button, Dropdown, Menu, Space } from 'antd';
import { ListFilter } from 'lucide-react';
import { TbCheck, TbClock, TbUser, TbX } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { SearchFilterInput, SmallTag } from '@/shared';
import { DropdownMenuPanel } from '@/shared/components/dropdowns/DropdownPanel';

import { HISTORY_TIME_RANGE_OPTIONS, HistoryTimeRange } from '../../../../helpers';

export interface HistoryFilterOwner {
    avatar?: string;
    id: string;
    initials: string;
    label: string;
}

interface HistoryFilterToolbarProps {
    activeFilterCount: number;
    disabled?: boolean;
    onOwnerChange: (ownerId: string) => void;
    onReset: () => void;
    onSearchChange: (value: string) => void;
    onTimeRangeChange: (timeRange: HistoryTimeRange) => void;
    ownerId: string;
    owners: HistoryFilterOwner[];
    search: string;
    timeRange: HistoryTimeRange;
}

const SelectedIndicator: FC<{ selected: boolean }> = ({ selected }): JSX.Element => (
    <TbCheck size='0.875rem' style={{ marginLeft: 'auto', opacity: selected ? 1 : 0 }} />
);

const HistoryFilterToolbar: FC<HistoryFilterToolbarProps> = ({
    activeFilterCount,
    disabled,
    onOwnerChange,
    onReset,
    onSearchChange,
    onTimeRangeChange,
    ownerId,
    owners,
    search,
    timeRange,
}): JSX.Element => {
    const { token } = useAntDesignTheme();

    const menu = (
        <DropdownMenuPanel
            style={{
                // viewport-layout-allow: the history filter is a floating menu constrained to the browser edge.
                maxHeight: 'min(30rem, calc(100vh - 8rem))',
                minWidth: '17rem',
                overflowY: 'auto',
            }}
        >
            <Menu.ItemGroup key='owners' title='Owner'>
                <Menu.Item key='owner-all' onClick={() => onOwnerChange('all')}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <TbUser size='1rem' style={{ color: token.colorTextQuaternary }} />
                        <span style={{ flex: 1 }}>All owners</span>
                        <SelectedIndicator selected={ownerId === 'all'} />
                    </span>
                </Menu.Item>
                {owners.map((owner) => (
                    <Menu.Item key={`owner-${owner.id}`} onClick={() => onOwnerChange(owner.id)}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Avatar size={16} src={owner.avatar}>
                                {owner.initials}
                            </Avatar>
                            <span style={{ flex: 1 }}>{owner.label}</span>
                            <SelectedIndicator selected={ownerId === owner.id} />
                        </span>
                    </Menu.Item>
                ))}
            </Menu.ItemGroup>
            <Menu.Divider />
            <Menu.ItemGroup key='time-range' title='Time frame'>
                {HISTORY_TIME_RANGE_OPTIONS.map((option) => (
                    <Menu.Item key={`time-${option.value}`} onClick={() => onTimeRangeChange(option.value)}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <TbClock size='1rem' style={{ color: token.colorTextQuaternary }} />
                            <span style={{ flex: 1 }}>{option.label}</span>
                            <SelectedIndicator selected={timeRange === option.value} />
                        </span>
                    </Menu.Item>
                ))}
            </Menu.ItemGroup>
        </DropdownMenuPanel>
    );

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
                padding: '0.75rem 0.625rem',
                borderBottom: `1px solid ${token.colorBorderSecondary}`,
            }}
        >
            <div style={{ flex: 1, minWidth: 0 }}>
                <SearchFilterInput
                    bordered
                    disabled={disabled}
                    placeholder='Search History...'
                    size='small'
                    value={search}
                    setValue={onSearchChange}
                />
            </div>
            <Space.Compact size='small' style={{ flex: 'none' }}>
                <Dropdown trigger={['click']} placement='bottomRight' popupRender={() => menu} disabled={disabled}>
                    <Button
                        aria-label='Filter version history'
                        disabled={disabled}
                        icon={<ListFilter size='1rem' style={{ color: token.colorTextQuaternary }} />}
                        size='small'
                    />
                </Dropdown>
                {activeFilterCount ? (
                    <Button
                        aria-label={`Clear ${activeFilterCount} active history filter${
                            activeFilterCount === 1 ? '' : 's'
                        }`}
                        disabled={disabled}
                        onClick={onReset}
                        size='small'
                        style={{ alignItems: 'center', background: token.colorFillTertiary, display: 'inline-flex' }}
                    >
                        <SmallTag compact message={activeFilterCount} />
                        <TbX size='1rem' style={{ color: token.colorTextTertiary }} />
                    </Button>
                ) : null}
            </Space.Compact>
        </div>
    );
};

export default HistoryFilterToolbar;
